import { useEffect, useState } from "react";

import { updateBranch } from "../api/branches";
import { NoScope, PaneHead, scopeProblem } from "./pane";
import { useSettingsScope } from "./settings-layout";
import styles from "./settings.module.css";
import { Toast } from "./toast";
import { useWriter } from "./use-writer";

/** 00 to 23, as the wall clock at the location reads them. */
const HOURS = Array.from({ length: 24 }, (_, hour) => hour);

function clock(hour: number): string {
  return `${String(hour).padStart(2, "0")}:00`;
}

/**
 * **The automatic WhatsApp reminder, controlled per location** (EB5,
 * 2026-09-29).
 *
 * It had no screen at all: always on, always 18:00 the evening before, so a
 * tester looking for reminders found nothing and assumed there were none. Two
 * settings, and only these two — Anthony's answer:
 *
 *   - **on or off.** Off stops the automatic message at this location; the
 *     calendar's *Remind* button still sends one by hand (WT5a).
 *   - **the hour, the evening before.** Always the evening before and always
 *     the location's own clock — an N-hours-before offset would message some
 *     patients while they sleep, which is why WT5b chose an evening at all.
 *
 * **Per location, from the rail's switcher**, like Opening hours: a practice
 * across two time zones sends at each one's own evening. Clinic-scoped on the
 * wire (`PATCH /branches/:id`), so no membership of the location is needed.
 */
export function RemindersPane(): React.JSX.Element {
  const { branches, branch, reloadBranches } = useSettingsScope();
  const problem = scopeProblem(branches, branch);
  const { busy, toast, dismiss, run } = useWriter();

  // From the location's own row — the form only draws once there is one, so
  // nothing here invents a default hour (*data is configured, not coded*).
  const [enabled, setEnabled] = useState(branch?.autoRemindersEnabled ?? true);
  const [hour, setHour] = useState(branch?.autoReminderHour ?? 0);

  // The stored values, again whenever the location or its saved row changes.
  useEffect(() => {
    if (branch === null) return;
    setEnabled(branch.autoRemindersEnabled ?? true);
    setHour(branch.autoReminderHour ?? 0);
  }, [branch]);

  const changed =
    branch !== null &&
    (enabled !== (branch.autoRemindersEnabled ?? true) || hour !== (branch.autoReminderHour ?? 0));

  return (
    <>
      <PaneHead title="Reminders" scope={branch?.name} />

      {problem !== null && problem !== "not-a-member" ? (
        <NoScope what="reminders" reason={problem} />
      ) : branch === null ? null : (
        <section className={styles.card} data-testid="reminders-pane">
          <div className={styles.cardHead}>
            <h2 className={styles.cardTitle}>Automatic WhatsApp reminder</h2>
          </div>

          <div className={styles.cardBody}>
            <p className={styles.lead}>
              Each patient booked at this location gets one WhatsApp message the evening before
              their appointment. The &ldquo;Remind&rdquo; button on an appointment still sends one
              by hand, whatever is set here.
            </p>

            <label className={styles.checkRow}>
              <input
                type="checkbox"
                checked={enabled}
                disabled={busy}
                onChange={(event) => setEnabled(event.target.checked)}
                data-testid="reminders-enabled"
              />
              <span>Send reminders automatically at this location</span>
            </label>

            <div className={styles.field}>
              <label className={styles.label} htmlFor="reminders-hour">
                Send at, the evening before
              </label>
              <select
                id="reminders-hour"
                className={styles.input}
                value={hour}
                disabled={busy || !enabled}
                onChange={(event) => setHour(Number(event.target.value))}
                data-testid="reminders-hour"
              >
                {HOURS.map((value) => (
                  <option key={value} value={value}>
                    {clock(value)}
                  </option>
                ))}
              </select>
            </div>

            <div className={styles.formActions}>
              <button
                type="button"
                className={styles.primary}
                disabled={busy || !changed}
                onClick={() => {
                  void run(
                    () =>
                      updateBranch(branch.id, {
                        autoRemindersEnabled: enabled,
                        autoReminderHour: hour,
                      }),
                    reloadBranches,
                    enabled
                      ? `Reminders go out at ${clock(hour)} the evening before.`
                      : "Automatic reminders are off at this location.",
                  );
                }}
                data-testid="reminders-save"
              >
                Save reminders
              </button>
            </div>
          </div>
        </section>
      )}

      {toast !== null && <Toast kind={toast.kind} message={toast.message} onDismiss={dismiss} />}
    </>
  );
}
